import { FC, Fragment, useEffect, useRef, useState } from 'react';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { selectQuiz } from 'store/quiz/quizSelectors';
import IQuiz from 'models/Quiz';
// Components
import AudioAlertDialog from 'dialogs/AudioAlert.dialog';
// Utilities
import { getIOSVersion } from 'utilities/Utilities';
import { Motion, spring } from 'react-motion';

type Props = {
  src: string;
  onEnded?: () => void;
  playbackRate?: number;
  startTime?: number;
  endTime?: number;
};

const AudioPlayer: FC<Props> = ({ src, onEnded, playbackRate = 1, startTime = 0, endTime }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [isEnded, setIsEnded] = useState(false);

  const quiz:IQuiz = useAppSelector(selectQuiz);

  const { open, toggle } = useToggle();

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
  
    const iOSVersion = getIOSVersion();

    audio.playbackRate = playbackRate;

    const handleTimeUpdate = () => {
      const duration = endTime ? endTime - startTime : audio.duration;
      setProgress(((audio.currentTime - startTime) / duration) * 100);
      if (endTime !== undefined && endTime > 0 && audio.currentTime >= endTime) {
        audio.pause();
        setIsEnded(true);
        if (onEnded) onEnded();
      }
    };
  
    const handleEnded = () => {
      setIsEnded(true);
      if (onEnded) onEnded();
    };
  
    const setupListeners = () => {
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("ended", handleEnded);
    };
  
    const cleanupListeners = () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  
    const playAudio = () => {
      if (startTime) {
        audio.currentTime = startTime;
      }
  
      setupListeners();
  
      audio
        .play()
        .catch((e) => console.warn("Play failed:", e));
  
      document.removeEventListener('click', playAudio);
    };
  
    if (!iOSVersion || iOSVersion >= 17) {
      setupListeners();
  
      audio
        .play()
        .then(() => {
          if (startTime) {
            audio.currentTime = startTime;
          }
        })
        .catch((e) => {
          console.warn('Autoplay blocked:', e);
          toggle();
          document.addEventListener('click', playAudio);
        });
    } else {
      toggle();
      document.addEventListener('click', playAudio);
    }
  
    return () => {
      cleanupListeners();
      document.removeEventListener('click', playAudio);
    };
    // eslint-disable-next-line
  }, [src, onEnded, playbackRate, startTime, endTime]);
  
  return (
    <Fragment>
      {open ? (
        <AudioAlertDialog onClose={toggle} />
      ) : null}

      <div className="w-full">
        <audio ref={audioRef} src={src} preload="auto" className="hidden" />
        {!isEnded && (
          <Motion style={{
            smoothProgress: spring(progress, {
              stiffness: 60,
              damping: 20,
            })
          }}>
            {values => (
              <div
                className="relative w-full bg-gray-200 border-4 border-gray-200 h-4 rounded-lg"
                style={{ marginBottom: 2 }}
              >
                <div className="absolute top-0 left-0 w-full h-2 opacity-25 rounded-lg z-0" style={{ backgroundColor: quiz.theme.progressBarColor }}></div>
                <div className="absolute top-0 left-0 h-2 rounded-lg z-10" style={{ width: `${values.smoothProgress}%`, backgroundColor: quiz.theme.progressBarColor }}></div>
              </div>
            ) }
          </Motion>
        )}
      </div>
    </Fragment>
  );
};

export default AudioPlayer;
